/* eslint-disable @typescript-eslint/no-explicit-any */
import { postAlbaran, postAlbaranPdf } from "api/Albaran.service"
import {
  postFactura,
  postFacturaPdf,
  putActualizarFactura,
} from "api/Factura.service"
import { postPedido, postPedidoPdf } from "api/Pedido.service"
import { postPresupuesto, postPresupuestoPdf } from "api/Presupuesto.service"
import { TIPO_DOCUMENTO_ENUM } from "api/Types/TipoDocumentoEnum"
import { DATE_YYYY_MM_DD } from "common/components/FilterMenu/constants"
import { formatDate } from "common/utils/date"
import { COLOR_PRIMARY } from "common/utils/theme"
import { TFunction } from "i18next"
import { capitalize } from "lodash"
import {
  ALBARANES,
  FACTURAS,
  FACTURAS_PROFORMA,
  PEDIDOS,
  PRESUPUESTOS,
} from "routes/RoutePaths"
import { backendDateFormat } from "utils/formatDate"

export const INITIAL_DOCUMENT_SERIE = "A"
export const INITIAL_DOCUMENT_CURRENT = "EUR"
export const DEFAULT_DOCUMENT_NUMBER = 1
export const DEFAULT_DOCUMENT_IMPUESTO = 21

export const DOCUMENT_SCREEN = {
  SELECT_CONTACT: 1,
  SELECT_ARTICULO: 2,
  FORM_COMPLETO: 3,
}

export const FORMAS_PAGO_DOC = {
  TRANSFERENCIA: "Transferencia",
  PAGO_DOMICILIADO: "Pago Domiciliado",
}

export const COLORS_PALETTE = [
  COLOR_PRIMARY,
  "#00d8ac",
  "#FFB200",
  "#FF3A29",
  "#353535",
]

export const TYPES_DOCUMENTOS = {
  PRESUPUESTO: "presupuesto",
  PEDIDO: "pedido",
  ALBARAN: "albaran",
  FACTURA: "factura",
  PROFORMA: "proforma",
  FACTURA_RECTIFICATIVA: "facturaRectificativa",
}

export const MODES_DOCUMENTOS = {
  VENTA: "venta",
  COMPRA: "compra",
}

export const getDocumentLabel = (type: string, t: TFunction): string => {
  switch (type) {
    case TYPES_DOCUMENTOS.PRESUPUESTO:
      return t("newDoc.presupuesto.quoteLabel")
    case TYPES_DOCUMENTOS.PEDIDO:
      return t("newDoc.pedido.orderLabel")
    case TYPES_DOCUMENTOS.ALBARAN:
      return t("newDoc.albaran.deliveryLabel")
    case TYPES_DOCUMENTOS.FACTURA:
      return t("newDoc.factura.invoiceLabel")
    case TYPES_DOCUMENTOS.PROFORMA:
      return t("newDoc.facturaProforma.label")
    case TYPES_DOCUMENTOS.FACTURA_RECTIFICATIVA:
      return t("newDoc.facturarRectifitiva.label")
    default:
      return t("newDoc.facturarRectifitiva.label")
  }
}
type FileType =
  | "Imagen"
  | "Pdf"
  | "Logo"
  | "Texto"
  | "Excel"
  | "Doc"
  | "Csv"
  | "Edi"
  | "Xml"
  | "Desconocido"
type FileTypeMap = {
  [key in FileType]: number
}

const fileTypes: { [key in Exclude<FileType, "Desconocido">]: string[] } = {
  Imagen: ["jpg", "jpeg", "png", "gif", "bmp", "tiff", "svg"],
  Pdf: ["pdf"],
  Logo: ["logo"],
  Texto: ["txt"],
  Excel: ["xls", "xlsx"],
  Doc: ["doc", "docx"],
  Csv: ["csv"],
  Edi: ["edi"],
  Xml: ["xml"],
}

const typeMap: FileTypeMap = {
  Imagen: 0,
  Pdf: 1,
  Logo: 2,
  Texto: 3,
  Excel: 4,
  Doc: 5,
  Csv: 6,
  Edi: 7,
  Xml: 8,
  Desconocido: -1,
}

export const handleOnHideCrearDocumento = (valor: string) => {
  switch (valor) {
    case TYPES_DOCUMENTOS.PRESUPUESTO:
      return PRESUPUESTOS
    case TYPES_DOCUMENTOS.PEDIDO:
      return PEDIDOS
    case TYPES_DOCUMENTOS.ALBARAN:
      return ALBARANES
    case TYPES_DOCUMENTOS.FACTURA:
      return FACTURAS
    case TYPES_DOCUMENTOS.PROFORMA:
      return FACTURAS_PROFORMA
    default:
      return FACTURAS
  }
}

export const adapterProfomaDocumento = (documentData: $TSMigrationFixMe) => ({
  cliente: {
    direccion: {
      calle: documentData.librado.direccion?.calle,
      ciudad: documentData.librado.direccion?.poblacionNombre,
      codigoPostal: documentData.librado.direccion?.codigoPostal,
      poblacion: documentData.librado.direccion?.provinciaNombre,
    },
    empresaId: documentData.librado?.empresaContactoId,
    telefono: documentData.librado?.telefono,
  },
  conceptos: documentData.conceptos.map((concepto: any) => {
    return {
      cantidad: concepto.cantidad,
      impuesto: {
        idave: concepto.impuestoArticuloTipoId,
        porcentaje: concepto.impuestoArticuloPorcentaje,
      },
      nombre: concepto.concepto,
      precio: concepto.precioVenta,
    }
  }),
  descuento: documentData.descuento ?? 0,
  divisa: documentData.divisa,
  fechaEmision: formatDate(documentData?.fechaEmision, DATE_YYYY_MM_DD),
  fechaVencimiento: documentData?.fechaVencimiento
    ? formatDate(documentData?.fechaVencimiento, DATE_YYYY_MM_DD)
    : null,
  importe: documentData.totales.totalValor,
  numeroDocumento: documentData.numeroDocumento,
  proveedor: {
    direccion: {
      calle: documentData.librador.direccion.calle,
      ciudad: documentData.librador.direccion.poblacionNombre,
      codigoPostal: documentData.librador.direccion.codigoPostal,
      poblacion: documentData.librador.direccion.provinciaNombre,
    },
    empresaId: documentData.librador.id,
    telefono: documentData.librador.telefono,
  },
  retencion: documentData?.retencionTipoId
    ? {
        id: documentData?.retencionTipoId,
        porcentaje: documentData?.retencionPorcentaje,
      }
    : null,
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const adapterPdfDocumento = (
  documentData: $TSMigrationFixMe
): $TSMigrationFixMe => {
  const tipoDoc = (): $TSMigrationFixMe => {
    if (documentData?.documentoType.type === TYPES_DOCUMENTOS.FACTURA)
      return TIPO_DOCUMENTO_ENUM.FACTURA
    if (documentData?.documentoType.type === TYPES_DOCUMENTOS.PEDIDO)
      return TIPO_DOCUMENTO_ENUM.PEDIDO
    if (documentData?.documentoType.type === TYPES_DOCUMENTOS.PRESUPUESTO)
      return TIPO_DOCUMENTO_ENUM.PRESUPUESTO
    if (documentData?.documentoType.type === TYPES_DOCUMENTOS.ALBARAN)
      return TIPO_DOCUMENTO_ENUM.ALBARAN
  }
  const adaptedData: $TSMigrationFixMe = {
    configuracion: {
      color: documentData?.config.colorSelected,
    },
    archivosAdjuntos: [],
    articuloDocumentos: documentData?.conceptos
      ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
        documentData?.conceptos?.map((concepto: $TSMigrationFixMe) => ({
          articulo: {
            concepto: concepto?.concepto ?? "",
            precioVenta: documentData.config.sinValorar
              ? null
              : concepto?.precioVenta ?? 0,
            tipoArticulo: concepto?.tipoArticulo ?? null,
          },
          cantidad: concepto?.cantidad ?? 0,
          ...(!documentData.config.sinValorar && {
            impuestoRepercutido: "IVA",
          }),
          porcentajeRepercutido: documentData.config.sinValorar
            ? null
            : concepto?.impuestoArticuloPorcentaje ?? null,
          totalRepercutido: documentData.config.sinValorar
            ? 0
            : concepto?.totalRepercutido ?? 0,
        }))
      : [],
    descuento: documentData?.descuento ?? null,
    descuentoValor:
      documentData?.totales?.descuentoValor > 0
        ? documentData?.totales?.descuentoValor
        : null,
    diasPago: documentData?.diasPago ?? null,

    divisa: documentData?.divisa ?? "€",
    documentoAjustesPorDefecto:
      documentData?.documentoAjustesPorDefecto ?? null,
    documentosAsociados: documentData?.documentosComercialesAsociadosPdf ?? [],
    fechaOperacion: documentData?.fechaOperacion
      ? formatDate(documentData?.fechaOperacion, DATE_YYYY_MM_DD)
      : null,
    ...(documentData.documentoType.mode === MODES_DOCUMENTOS.VENTA && {
      fechaVencimiento: documentData?.fechaVencimiento
        ? formatDate(documentData?.fechaVencimiento, DATE_YYYY_MM_DD)
        : null,
    }),
    formaPago: documentData?.formaPago ?? null,
    importe: documentData.config.sinValorar
      ? null
      : documentData?.totales.totalValor ?? null,
    impuestoRetenido: documentData.config.sinValorar
      ? null
      : documentData?.retencionPorcentaje
      ? "Retencion"
      : null,
    librado: {
      codigoPostal: documentData?.librado.direccion.codigoPostal,
      direccion: documentData?.librado.direccion.calle,
      email: documentData?.librado.email,
      nif: documentData?.librado.nif ?? documentData?.librado.cif,
      poblacion: documentData?.librado.direccion.poblacionNombre,
      provincia: documentData?.librado.direccion.provinciaNombre,
      razonSocial: documentData?.librado.razonSocial,
      telefono: documentData?.librado.telefono,
    },
    librador: {
      codigoPostal: documentData?.librador.direccion.codigoPostal,
      direccion: documentData?.librador.direccion.calle,
      email: documentData?.librador.email,
      nif: documentData?.librador.nif ?? documentData?.librador.cif,
      poblacion: documentData?.librador.direccion.poblacionNombre,
      provincia: documentData?.librador.direccion.provinciaNombre,
      razonSocial: documentData?.librador.razonSocial,
      telefono: documentData?.librador.telefono,
    },
    logoAsFileBase64:
      documentData?.logo?.base64 ??
      documentData?.logo?.fileAsBase64 ??
      documentData?.logoAsBase64 ??
      null,
    logoExtension: null,
    observaciones: documentData?.observaciones ?? null,
    porcentajeRetenido: documentData?.retencionPorcentaje ?? null,
    retencionValor: documentData?.totales.retencionTotal ?? null,
    serie: documentData?.serie ?? null,
    subtotal: documentData?.totales.subtotal ?? 0,
    tipoDocumento: tipoDoc(),
    tiposImpuestos: documentData?.totales.impuestosAgrupados
      ? documentData?.totales.impuestosAgrupados.map(
          (impuesto: $TSMigrationFixMe) => ({
            impuestoPorcentaje: impuesto?.impuestoArticuloPorcentaje,
            impuestoValor: impuesto?.sumatorio,
            tipoImpuesto: "IVA",
          })
        )
      : [],
    fechaEmision: formatDate(documentData?.fechaEmision, DATE_YYYY_MM_DD),
    numero: documentData?.numero ?? null,
    iban: documentData?.formaPagoIban ?? null,
  }

  return adaptedData
}

export const toBase64 = async (
  file: File
): Promise<string | ArrayBuffer | null> => {
  const reader = new FileReader()

  return new Promise((resolve, reject) => {
    reader.onload = () => resolve(reader.result)
    reader.onerror = (error) => reject(error)
    reader.readAsDataURL(file)
  })
}

export const adapterNewContact = (event: $TSMigrationFixMe) => {
  return {
    razonSocial: event.razonSocial,
    nif: event.cif,
    codigoPais: event.codigoPais,
    direccion: {
      calle: event.direccion,
      codigoPostal: event.cp,
      poblacionId: event.poblacionId,
      poblacionNombre: event.poblacion,
    },
    email: event.email,
    telefono: event.telefono,
  }
}

export const getFileType = (file: File): number => {
  const extension = file.name.split(".").pop()?.toLowerCase()

  if (!extension) {
    return typeMap.Desconocido
  }

  const type = (Object.keys(fileTypes) as Array<keyof typeof fileTypes>).find(
    (key) => fileTypes[key].includes(extension)
  ) as FileType | undefined

  return type ? typeMap[type] : typeMap.Desconocido
}

export const adapterDocumento = (documentData: $TSMigrationFixMe) => {
  const tipoDoc = () => {
    switch (documentData?.documentoType?.type) {
      case TYPES_DOCUMENTOS.FACTURA:
        return TIPO_DOCUMENTO_ENUM.FACTURA
      case TYPES_DOCUMENTOS.PEDIDO:
        return TIPO_DOCUMENTO_ENUM.PEDIDO
      case TYPES_DOCUMENTOS.PRESUPUESTO:
        return TIPO_DOCUMENTO_ENUM.PRESUPUESTO
      case TYPES_DOCUMENTOS.ALBARAN:
        return TIPO_DOCUMENTO_ENUM.ALBARAN
      case TYPES_DOCUMENTOS.FACTURA_RECTIFICATIVA:
        return TIPO_DOCUMENTO_ENUM.FACTURA
      default:
        return null
    }
  }

  const setFicheros = (documentData: $TSMigrationFixMe) => {
    const resultFichero = documentData.archivosAdjuntos?.map(
      (file: $TSMigrationFixMe) => {
        if (file && file.base64 && file.archivo && file.archivo.path) {
          return {
            fileAsBase64: file.base64,
            nombre: file.archivo.path,
            tipo: getFileType(file.archivo),
          }
        }

        if (file && file.base64 && !file.archivo) {
          return {
            fileAsBase64: file.base64,
            tipo: 2,
          }
        }

        return null
      }
    )

    const validResults = resultFichero?.filter(
      (result: $TSMigrationFixMe) => result !== null
    )

    const logoExists = documentData.archivosAdjuntos?.some(
      (file: $TSMigrationFixMe) => !file.archivo
    )
    if (
      !logoExists &&
      documentData.logo &&
      (documentData.logo.base64 || documentData.logo.fileAsBase64)
    ) {
      const logoFile = {
        fileAsBase64:
          documentData.logo.base64 || documentData.logo.fileAsBase64,
        tipo: 2,
      }
      validResults?.push(logoFile)
    }

    return validResults
  }

  const adaptedData: $TSMigrationFixMe = {
    ficheros: setFicheros(documentData),
    tipoDocumento: tipoDoc(),
    configuracion: {
      color: documentData?.config?.colorSelected,
    },
    articulosDocumento: documentData?.conceptos
      ? documentData?.conceptos?.map(
          (concepto: $TSMigrationFixMe, index: number) => ({
            articulo: {
              concepto: concepto?.concepto ?? "",
              precioVenta: documentData.config.sinValorar
                ? null
                : concepto?.precioVenta ?? 0,
              tipoArticulo: concepto?.tipoArticulo ?? 1,
            },
            cuentaContableCategoriaId:
              documentData.documentoType.type === TYPES_DOCUMENTOS.FACTURA &&
              documentData.config.cuentaContableMultiple
                ? concepto?.cuentaContableId
                : null,
            cantidad: concepto?.cantidad ?? 0,
            ...(!documentData.config.sinValorar && {
              impuestoEmpresaRepercutidoId: concepto?.impuestoArticuloTipoId,
            }),
            orden: index,
            ...(!documentData.config.sinValorar && {
              tarifaImpuesto: concepto?.impuestoArticuloPorcentaje,
            }),
          })
        )
      : [],
    ...(documentData.modoEditarBorrador &&
      !documentData?.esBorrador && {
        borradorId: documentData.id,
      }),
    cliente: {
      cif: documentData?.librado?.nif || documentData?.librado?.cif,
      direccion: {
        calle: documentData?.librado?.direccion?.calle,
        codigoPostal: documentData?.librado?.direccion?.codigoPostal,
        poblacionId: documentData?.librado?.direccion?.poblacionId,
        poblacionExtranjera:
          documentData?.librado?.direccion?.poblacionExtranjera,
      },
      email: documentData?.librado?.email,
      razonSocial: documentData?.librado?.razonSocial,
      telefono: documentData?.librado?.telefono,
    },
    descuento: documentData?.descuento ?? null,
    diasPago: documentData?.diasPago ?? null,
    divisa: documentData?.divisa ?? "€",
    documentoImportadoId: null,
    documentosAjustesPorDefectoCreationDto: null,
    ...(!documentData.isToUpdate && {
      esBorrador: documentData?.esBorrador ?? false,
    }),
    // TODO: OJO CON EL FORMATO
    fechaEmision: formatDate(documentData?.fechaEmision, DATE_YYYY_MM_DD),
    fechaOperacion: documentData?.fechaOperacion
      ? formatDate(documentData?.fechaOperacion, DATE_YYYY_MM_DD)
      : null,
    ...(documentData.documentoType.mode === MODES_DOCUMENTOS.VENTA && {
      fechaVencimiento: documentData?.fechaVencimiento
        ? formatDate(documentData?.fechaVencimiento, DATE_YYYY_MM_DD)
        : null,
    }),
    formaPago: documentData?.formaPago ?? null,
    iban: documentData?.formaPagoIban ?? null,
    importe: documentData.config.sinValorar
      ? null
      : documentData?.totales.totalValor ?? null,
    impuestoEmpresaRetenidoId: documentData.config.sinValorar
      ? null
      : documentData?.retencionTipoId ?? null,
    libradoId: documentData.librado.id,
    libradorId: documentData.librador.id,
    observaciones: documentData?.observaciones ?? null,
    proveedor: {
      cif: documentData?.librador?.nif || documentData?.librador?.cif,
      direccion: {
        calle: documentData?.librador?.direccion?.calle,
        codigoPostal: documentData?.librador?.direccion?.codigoPostal,
        poblacionId: documentData?.librador?.direccion?.poblacionId,
        poblacionExtranjera:
          documentData?.librador?.direccion?.poblacionExtranjera,
      },
      email: documentData?.librador?.email,
      razonSocial: documentData?.librador?.razonSocial,
      telefono: documentData?.librador?.telefono,
    },
    serie: documentData?.serie ?? null,
    tarifaImpuesto: documentData?.retencionPorcentaje ?? null,
    tipoDocumentoComercial:
      documentData.documentoType.mode === MODES_DOCUMENTOS.VENTA ? 1 : 0,
    ...(documentData.documentoType.type ===
      TYPES_DOCUMENTOS.FACTURA_RECTIFICATIVA && {
      facturaRectificada: documentData?.facturaRectificativa,
    }),
  }

  const type = documentData?.documentoType?.type

  if (type !== TYPES_DOCUMENTOS.PRESUPUESTO) {
    adaptedData.documentosComercialesAsociadosIds =
      documentData.documentosComercialesAsociadosId
  }
  if (
    type !== TYPES_DOCUMENTOS.PRESUPUESTO ||
    type === TYPES_DOCUMENTOS.FACTURA
  ) {
    adaptedData.fechaEntrega = null
  }

  if (type === TYPES_DOCUMENTOS.FACTURA) {
    Object.assign(adaptedData, {
      cuentaContableCategoriaId: !documentData.config.cuentaContableMultiple
        ? documentData.cuentaContableId
        : null,
      facturaProformaId: documentData?.facturaProformaId ?? null,
      tags: documentData?.tags ?? null,
      totalRepercutido: null,
      totalRetenido: null,
    })
  }

  return adaptedData
}

export const generateAndDownloadPdf = async (
  documentType: $TSMigrationFixMe,
  values: $TSMigrationFixMe,
  downloadFile: $TSMigrationFixMe
) => {
  const pdfGenerators: $TSMigrationFixMe = {
    factura: postFacturaPdf,
    albaran: postAlbaranPdf,
    pedido: postPedidoPdf,
    presupuesto: postPresupuestoPdf,
  }

  if (pdfGenerators[documentType]) {
    const pdf = await pdfGenerators[documentType](adapterPdfDocumento(values))
    const fileName = `${capitalize(documentType)}_${values.serie}-${String(
      values.numero ?? backendDateFormat(new Date())
    ).padStart(4, "0")}`
    downloadFile.handleDownloadPdf(fileName, pdf)
  }
}

export const generateDocument = async (
  documentType: $TSMigrationFixMe,
  values: $TSMigrationFixMe
) => {
  const pdfGenerators: $TSMigrationFixMe = {
    factura: postFactura,
    albaran: postAlbaran,
    pedido: postPedido,
    presupuesto: postPresupuesto,
    facturaRectificativa: postFactura,
  }

  if (values.modoEditarBorrador && values.esBorrador) {
    return putActualizarFactura(
      values.id,
      adapterDocumento({ ...values, isToUpdate: true })
    )
  }
  if (values.modoEditarBorrador && !values.esBorrador) {
    await putActualizarFactura(
      values.id,
      adapterDocumento({ ...values, isToUpdate: true })
    )
    return pdfGenerators[documentType](adapterDocumento(values))
  }

  const resp = await pdfGenerators[documentType](adapterDocumento(values))
  return resp
}

export const esFechaOperacionValida = (fechaOperacion: Date): boolean => {
  const emision = new Date()
  const operacion = new Date(fechaOperacion)

  emision.setHours(0, 0, 0, 0)
  operacion.setHours(0, 0, 0, 0)

  const diaEmision = emision.getDate()
  const inicioPeriodo = new Date(emision)

  if (diaEmision <= 15) {
    inicioPeriodo.setMonth(inicioPeriodo.getMonth() - 1)
  }

  inicioPeriodo.setDate(1)

  return operacion >= inicioPeriodo && operacion <= emision
}
